import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, computed, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {  NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import {  NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { Address } from '../../../interfaces/referralSections.interface';
import { NzAriaLabelDirective } from '../../directives/nz-aria-label.directive';
import { SectionsService } from '../../../services/sections.service';
import { CountyValidationService } from '../../services/county-validation.service';
import { LookupValueItem } from '../../../interfaces/lookup.interface';
import { LookupService } from '../../../services/lookup.service';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DropdownSelectComponent } from '../dropdown-select/dropdown-select.component';
import { NgZorroModule } from '../../modules/ng-zorro/ng-zorro.module';
import { COUNTY_REQUIRED_ENROLLMENT_TYPES, STATIC_DATA } from '../../constants/static-data.constants';

const key = 'TA93-MT53-WE43-EC19';

@Component({
  selector: 'ppl-address',
  standalone: true,
  imports: [
    NzFormModule,
    NzInputModule,
    NzSelectModule,
    NzIconModule,
    FormsModule,
    ReactiveFormsModule,
    NzSwitchModule,
    CommonModule,
    NzAriaLabelDirective,
    AutoCompleteModule,
    DropdownSelectComponent,
    NgZorroModule
  ],
  templateUrl: './address.component.html',
  styleUrl: './address.component.css',
})
export class AddressComponent implements OnInit, OnChanges {
  private countyValidationService = inject(CountyValidationService);
  statesListDropdown: LookupValueItem[] = [];
  @Input() addressForm!: FormGroup;
  @Input() mode = 'edit';
  @Input() data: Address;
  @Input() is_permanent = true;
  @Input() showPSD = true;


  isCountyRequired: boolean = false;

  private _permanentValidationErrors: any = {};

  @Input() set permanentValidationErrors(value) {
    this._permanentValidationErrors = value || {};
  }

  get permanentValidationErrors() {
    return this._permanentValidationErrors;
  }

  private _mailingValidationErrors: any = {};

  @Input() set mailingValidationErrors(value) {
    this._mailingValidationErrors = value || {};
  }

  get mailingValidationErrors() {
    return this._mailingValidationErrors;
  }

  counties = computed(() => this.countyValidationService.counties());

  selectedValue = null;
  listOfOptions: { value: string; text: string }[] = [];
  nzFilterOptions = (): boolean => true;
  fullAddress = '';

  get isConsumerFlow(): boolean {
    return this.countyValidationService.isConsumerFlow
  }

  constructor(
    private httpClient: HttpClient,
    private sectionsService: SectionsService,
    protected lookupService: LookupService
  ) {
    this.data = {
      street1: '',
      street2: '',
      city: '',
      state: '',
      zipCode: '',
      countyOrRegion: '',
      psd_code: '',
      mailing: false,
    };
  }

  ngOnInit(): void {
    const flow = this.sectionsService.getFlow();
    const enrollmentType = this.sectionsService.getEnrollmentType();
    const isCountyRequiredProviderRole =
      flow === STATIC_DATA.PROVIDER_ENROLLMENT &&
      Array.isArray(enrollmentType) &&
      COUNTY_REQUIRED_ENROLLMENT_TYPES.some((role) => enrollmentType.includes(role));
    this.isCountyRequired =
      flow === STATIC_DATA.PARTICIPANT_ENROLLMENT ||
      flow === STATIC_DATA.REFERRAL ||
      isCountyRequiredProviderRole;
    this.countyValidationService.getCounties();
    this.statesListDropdown = this.lookupService.getLookupValue('STATE');
    console.log('states', this.statesListDropdown);

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['addressForm']) {
      const addressForm = changes['addressForm'].currentValue;
      const countyVal = addressForm.value.countyOrRegion;
      if(countyVal && !this.countyValidationService.isCountyValid(countyVal)) {
        this.countyValidationService.addCounty(countyVal);
        this.addressForm.patchValue({
          countyOrRegion: countyVal
        });
      }
    }
  }

  onSearch(value: string): void {
    if (value && value.length > 3) {
      this.httpClient
        .get(
          `https://api.addressy.com/Capture/Interactive/Find/v1.10/json3.ws?Key=${key}&Text=${value}&IsMiddleware=True&Origin=44.182,-84.506&Countries=US,CA&Limit=10&Language=&Bias=&Filters=&GeoFence=`,
        )
        .subscribe((data: any) => {
          const listOfOptions: { value: string; text: string }[] = [];
          data.Items.forEach((result: { Text: string }) => {
            listOfOptions.push({
              value: result.Text,
              text: result.Text,
            });
          });

          this.listOfOptions = listOfOptions;
        });
    }
  }

  validateAddress(value: string) {
    const body = {
      Key: `${key}`,
      Options: {
        Enhance: true,
      },
      Addresses: [
        {
          Address1: `${value}`,
          Address2: '',
          Country: 'US',
          Locality: '',
          PostalCode: '',
        },
      ],
    };
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    this.httpClient
      .post(
        `https://api.addressy.com/Cleansing/International/Batch/v1.00/json4.ws`,
        body,
        { headers: headers },
      )
      .subscribe((data: any) => {
        if (data && data[0].Matches) {
          this.extractAddress(data[0].Matches[0]);
        }
      });
  }

  extractAddress(address: {
    Address1: string;
    PostalCode: string;
    AdministrativeArea: string;
    Locality: string;
    SubAdministrativeArea: string;
    Address: string;
  }) {
    this.addressForm.patchValue({
      street1: address.Address1,
      street2: '',
      zipCode: address.PostalCode,
      state: address.AdministrativeArea,
      city: address.Locality,
      countyOrRegion: address.SubAdministrativeArea,
    });
    const countyName = address.SubAdministrativeArea;
    if (!this.countyValidationService.isCountyValid(countyName)) {
      this.countyValidationService.addCounty(countyName);
    }
    this.fullAddress = address.Address;
  }
}
